import React from "react";
import { DocumentTextIcon, CurrencyDollarIcon, WrenchScrewdriverIcon, ReceiptRefundIcon } from "@heroicons/react/24/outline";
import { useCompanyDashboard } from "../../Hooks/useCompanyDashboard";

export default function WorkflowSection() {
    const { statistics, quotesSummary, invoicesSummary, isLoading, isError } = useCompanyDashboard();

    if (isLoading) {
        return (
            <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-5">Workflow</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 animate-pulse">
                            <div className="h-8 bg-gray-200 rounded w-32 mb-4" />
                            <div className="h-12 bg-gray-200 rounded w-20" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="text-center py-8">
                <p className="text-red-600">Failed to load workflow data. Please try again later.</p>
            </div>
        );
    }

    return (
        <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-5">Workflow</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {/* Requests */}
                <div className="relative bg-white rounded-2xl shadow-sm border border-gray-100 p-6 overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-amber-600" />
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center text-white">
                                <DocumentTextIcon className="w-5 h-5" />
                            </div>
                            <h3 className="font-semibold text-gray-900">Requests</h3>
                        </div>
                    </div>
                    <p className="text-4xl font-bold text-amber-600">{statistics.total_offers_submitted}</p>
                    <div className="mt-4 space-y-1 text-sm">
                        <p className="text-gray-500">Accepted <span className="font-medium text-gray-900">({statistics.accepted_offers_count})</span></p>
                        <p className="text-gray-500">Total Jobs <span className="font-medium text-gray-900">({statistics.total_jobs_count})</span></p>
                    </div>
                </div>

                {/* Quotes */}
                <div className="relative bg-white rounded-2xl shadow-sm border border-gray-100 p-6 overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-rose-500 to-rose-600" />
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-rose-500 to-rose-600 rounded-lg flex items-center justify-center text-white">
                                <CurrencyDollarIcon className="w-5 h-5" />
                            </div>
                            <h3 className="font-semibold text-gray-900">Quotes</h3>
                        </div>
                    </div>
                    <p className="text-4xl font-bold text-rose-600">{quotesSummary.quotes_accepted_today.count}</p>
                    <p className="text-sm text-gray-600 mt-1">Accepted Today</p>
                    <div className="mt-4 space-y-1 text-sm">
                        <p className="text-gray-500">Completed Jobs <span className="font-medium text-gray-900">({quotesSummary.quotes_with_completed_jobs.count})</span></p>
                        <p className="text-gray-500">Remaining <span className="font-medium text-gray-900">({quotesSummary.quotes_remaining.count})</span></p>
                    </div>
                </div>

                {/* Jobs */}
                <div className="relative bg-white rounded-2xl shadow-sm border border-gray-100 p-6 overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-emerald-600" />
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center text-white">
                                <WrenchScrewdriverIcon className="w-5 h-5" />
                            </div>
                            <h3 className="font-semibold text-gray-900">Jobs</h3>
                        </div>
                    </div>
                    <p className="text-4xl font-bold text-emerald-600">{statistics.total_jobs_count}</p>
                    <p className="text-sm text-gray-600 mt-1">Total Active</p>
                    <div className="mt-4 space-y-1 text-sm">
                        <p className="text-gray-500">Net Profit <span className="font-medium text-emerald-600">${statistics.net_profit_formatted}</span></p>
                    </div>
                </div>

                {/* Invoices */}
                <div className="relative bg-white rounded-2xl shadow-sm border border-gray-100 p-6 overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 to-cyan-600" />
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg flex items-center justify-center text-white">
                                <ReceiptRefundIcon className="w-5 h-5" />
                            </div>
                            <h3 className="font-semibold text-gray-900">Invoices</h3>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <p className="text-4xl font-bold text-cyan-600">{invoicesSummary.total_invoices_count}</p>
                        <p className="text-lg font-medium text-gray-700">${invoicesSummary.formatted_total_profit}</p>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Total Profit</p>
                    <div className="mt-4 space-y-1 text-sm">
                        <p className="text-gray-500">Recent Invoices <span className="font-medium text-gray-900">({invoicesSummary.recent_invoices.length})</span></p>
                    </div>
                </div>
            </div>
        </div>
    );
}